/**
 * Node Builder - Constructs graph query nodes from entity blocks
 *
 * Builds node structures with labels, filters, and relationships
 * from EntityBlock definitions.
 *
 * @module queryBuilderCore/builders/nodeBuilder
 */

import {
  buildClassFilterFromAdTypes,
  extractAdObjectTypeFilters,
} from '@/sections/insights/queryBuilder/legacy/entities/activeDirectory/ActiveDirectoryFilterUtils';
import { processFilterGroup } from '@/sections/insights/queryBuilder/legacy/processors/filterGroupProcessor';
import {
  EntityBlock,
  QueryFilter,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';
import { getEntityLabels } from '@/utils/mappings.util';

/**
 * Builds a graph query node from an entity block
 *
 * @param block - Entity block containing entity type and filter groups
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param technologyFilterOptions - Available technology filter options
 * @param seedFilterOptions - Available seed filter options
 * @param integrationValues - List of integration source values
 * @param queryContentTab - Current query content tab context
 * @returns Node structure with labels, filters, and relationships
 */
export function buildNodeFromBlock(
  block: EntityBlock,
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  technologyFilterOptions?: Record<string, unknown>,
  seedFilterOptions?: Record<string, unknown>,
  integrationValues?: string[],
  queryContentTab?: string
): {
  labels: string[];
  filters: QueryFilter[];
  relationships: Relationship[];
} | null {
  if (!block || !block.entityType) {
    return null;
  }

  const allDirectFilters: QueryFilter[] = [];
  const allRelationships: Relationship[] = [];

  block.filterGroups.forEach(group => {
    if (group.filters.length === 0) return;

    const { directFilters, relationships } = processFilterGroup(
      group,
      block.entityType,
      assetFilterOptions,
      vulnFilterOptions,
      technologyFilterOptions,
      seedFilterOptions,
      integrationValues,
      queryContentTab
    );

    allDirectFilters.push(...directFilters);
    allRelationships.push(...relationships);
  });

  let labels = getEntityLabels(block.entityType);

  if (block.entityType === 'ActiveDirectory') {
    labels = ['ADObject'];

    const {
      filters: cleanedFilters,
      types: adObjectTypes,
      negated: hasNegatedAdObjectTypes,
    } = extractAdObjectTypeFilters(allDirectFilters);

    allDirectFilters.length = 0;
    allDirectFilters.push(...cleanedFilters);

    const classFilter = buildClassFilterFromAdTypes<QueryFilter>(
      adObjectTypes,
      hasNegatedAdObjectTypes
    );

    if (classFilter) {
      allDirectFilters.push(classFilter);
    }
  }

  return {
    labels,
    filters: allDirectFilters,
    relationships: allRelationships,
  };
}
